/**
 * Moving one row of a list to another place by holding it and sliding it up or down: where it lands and
 * which rows make room. Only arithmetic here; the pointer and the screen are in ui/useReorder.ts.
 */

/** A row as it stood when it was taken: its top and its height, in the list's own coordinates. */
export interface RowBox {
  top: number;
  height: number;
}

/** The list with one item moved to another place; the others close up and make room. */
export function moved<T>(list: readonly T[], from: number, to: number): T[] {
  const next = list.slice();
  const place = Math.max(0, Math.min(next.length - 1, to));
  if (from < 0 || from >= next.length || from === place) return next;
  next.splice(place, 0, ...next.splice(from, 1));
  return next;
}

/**
 * The place a row lands at, `offset` pixels from where it was taken. It passes a neighbour as soon as
 * its leading edge is beyond that neighbour's middle, so half a row is enough, whatever their heights.
 */
export function landing(rows: readonly RowBox[], from: number, offset: number): number {
  const taken = rows[from];
  if (taken === undefined) return from;
  let to = from;
  if (offset > 0) {
    const edge = taken.top + taken.height + offset;
    for (let index = from + 1; index < rows.length; index += 1) {
      const row = rows[index]!;
      if (edge <= row.top + row.height / 2) break;
      to = index;
    }
  } else if (offset < 0) {
    const edge = taken.top + offset;
    for (let index = from - 1; index >= 0; index -= 1) {
      const row = rows[index]!;
      if (edge >= row.top + row.height / 2) break;
      to = index;
    }
  }
  return to;
}

/** How far a row moves aside while another one, taken at `from`, would land at `to`: `space` up or down. */
export function aside(index: number, from: number, to: number, space: number): number {
  if (from < index && index <= to) return -space;
  if (to <= index && index < from) return space;
  return 0;
}

/** How far the taken row may go: never above the first row, never below the last one. */
export function within(rows: readonly RowBox[], from: number, offset: number): number {
  const taken = rows[from];
  const first = rows[0];
  const last = rows.at(-1);
  if (taken === undefined || first === undefined || last === undefined) return 0;
  const up = first.top - taken.top;
  const down = last.top + last.height - (taken.top + taken.height);
  return Math.max(up, Math.min(down, offset));
}
